let warned = false
const warn = (key: string, ...args: Array<any>) => {
  if (warned) return
  warned = true
  console.warn(
    'storage function called with no namespace.  this is a no-op, key: ',
    key,
    ...args,
  )
}

const wrap =
  (
    namespace: string | undefined,
    func: (key: string, ...args: Array<any>) => any,
  ) =>
  (key: string, ...args: Array<any>) => {
    const finalKey = `[${namespace}]/[${key}]`
    return namespace ? func(finalKey, ...args) : warn(key, ...args)
  }

const retrieve = (key: string): any | undefined => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : undefined // Handle null item
  } catch (_e) {
    return undefined
  }
}

const store = (key: string, value: any): void => {
  try {
    if (value !== undefined && value !== null) {
      // Be more explicit for removal
      localStorage.setItem(key, JSON.stringify(value))
    } else {
      localStorage.removeItem(key)
    }
  } catch (error) {
    console.warn(`storage error, key: '${key}', value: `, value, error)
  }
}

const has = (key: string): boolean => localStorage.getItem(key) !== null // Check for non-null

const makeClear = (namespace: string) => (): void => {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach((key: string) => {
      if (key.startsWith(`[${namespace}]/[`)) {
        localStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.warn(`error trying to clear namespace '${namespace}'`, error)
  }
}

const getAll = (namespace: string): { [key: string]: any } | undefined => {
  try {
    const keys = Object.keys(localStorage)
    const result: { [key: string]: any } = Object.create(null)
    keys.forEach((key: string) => {
      const prefix = `[${namespace}]/[`
      if (key.startsWith(prefix)) {
        const itemKey = key.replace(prefix, '').slice(0, -1)
        result[itemKey] = retrieve(key) // retrieve already handles parsing
      }
    })
    return result
  } catch (error) {
    console.warn(`error trying to get all for namespace '${namespace}'`, error)
    return undefined // Return undefined on error
  }
}

const makeGetAll =
  (namespace: string) => (): { [key: string]: any } | undefined =>
    getAll(namespace)

const log = console.log.bind(console)

const makePrintAll = (namespace: string) => (): void => {
  const data = getAll(namespace)
  log(`namespace: '${namespace}'\n\n`, JSON.stringify(data, null, 2))
}

export type StorageInstance = {
  get: (key: string, ...args: Array<any>) => any | undefined
  set: (key: string, value: any, ...args: Array<any>) => void
  has: (key: string, ...args: Array<any>) => boolean
  clear: () => void
  getAll: () => { [key: string]: any } | undefined
  printAll: () => void
}

const makeStorage = (namespace?: string): StorageInstance => ({
  get: wrap(namespace, retrieve),
  set: wrap(namespace, store),
  has: wrap(namespace, has),
  clear: makeClear(namespace!),
  getAll: makeGetAll(namespace!),
  printAll: makePrintAll(namespace!),
})

export { retrieve, store, has }

export default makeStorage
